/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { tabBarListContentStyle } from '../../shared/layout/tabBarListContentStyle'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { InventoryHeaderSection } from '../components/InventoryHeaderSection';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal'
import { PosCatalogStockFiltersPanel } from '../../shared/filters/PosCatalogStockFiltersPanel';
import { InventoryStockCard } from '../components/InventoryStockCard';
import { InventoryPendingTransfersBanner } from '../components/InventoryPendingTransfersBanner';
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter';
import { useInventoryScreen } from './useInventoryScreen';
import { InventoryScreenModals } from './InventoryScreenModals';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { currencyFormatter } from '../application/inventoryUtils';
import type { StockSort, StockStatusFilter } from '../../inventory/stock/public-api';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { styles as inventoryScreenStyles } from './InventoryScreen.styles';

const STOCK_LIST_WEB_EXTRA_BOTTOM = 16;

const STOCK_SORT_OPTIONS: Array<{ id: StockSort; label: string }> = [
  { id: 'name_asc', label: 'Nombre (A-Z)' },
  { id: 'name_desc', label: 'Nombre (Z-A)' },
  { id: 'price_asc', label: 'Precio (menor a mayor)' },
  { id: 'price_desc', label: 'Precio (mayor a menor)' },
  { id: 'stock_asc', label: 'Cantidad (menor a mayor)' },
  { id: 'stock_desc', label: 'Cantidad (mayor a menor)' },
  { id: 'cost_asc', label: 'Costo (menor a mayor)' },
  { id: 'cost_desc', label: 'Costo (mayor a menor)' },
  { id: 'value_asc', label: 'Valor stock (menor a mayor)' },
  { id: 'value_desc', label: 'Valor stock (mayor a menor)' },
  { id: 'expiry_asc', label: 'Vencimiento (próximo primero)' },
  { id: 'expiry_desc', label: 'Vencimiento (lejano primero)' },
  { id: 'variant_label_asc', label: 'Variante (A-Z)' },
  { id: 'variant_label_desc', label: 'Variante (Z-A)' },
];

export const InventoryScreen = () => {
  useEffect(() => {
    ensureTicketFonts();
  }, []);
  const inventoryVm = useInventoryScreen();
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const { layoutWide: isWideInventoryLayout } = useTabWorkspaceLayout();

  const formatCurrencyCb = useCallback(
    (v: number) => currencyFormatter.format(v),
    [],
  );

  const { refreshing, onRefresh } = useScreenLoad(
    route.name,
    async (opts) => { await inventoryVm.global.loadFn(opts); }
  );

  const hasStockSearch = inventoryVm.stockList.searchQuery.trim().length > 0 || inventoryVm.stockList.pinnedStockProduct != null;
  const hasActiveStockQuery =
    hasStockSearch ||
    inventoryVm.stockList.stockFilter !== 'all' ||
    inventoryVm.stockList.stockCategoryFilter !== 'all' ||
    inventoryVm.stockList.stockBrandFilter != null ||
    inventoryVm.stockList.stockSupplierFilter != null;

  const clearStockFilters = useCallback(() => {
    inventoryVm.stockList.setSearchQuery('');
    inventoryVm.stockList.clearPinnedStockSearch();
    inventoryVm.stockList.applyStockQuickViewFilter('all');
    inventoryVm.stockList.applyStockCategoryFilter('all');
    inventoryVm.stockList.applyStockBrandFilter(null);
    inventoryVm.stockList.applyStockSupplierFilter(null);
  }, [inventoryVm.stockList]);

  const handleToggleStockFilter = useCallback(
    (filter: StockStatusFilter) => {
      if (inventoryVm.stockList.stockFilter === filter) {
        inventoryVm.stockList.applyStockQuickViewFilter('all');
      } else {
        inventoryVm.stockList.applyStockQuickViewFilter(filter);
      }
    },
    [inventoryVm.stockList.stockFilter, inventoryVm.stockList.applyStockQuickViewFilter],
  );

  const scrollContentStyle = useMemo(
    () =>
      tabBarListContentStyle({
        bottomInset: insets.bottom,
        webExtraBottom: STOCK_LIST_WEB_EXTRA_BOTTOM,
        baseContentStyle: inventoryScreenStyles.scrollContent,
        emptyGrowStyle:
          inventoryVm.stockList.filteredStocks.length === 0
            ? inventoryScreenStyles.scrollContentEmpty
            : null,
      }),
    [insets.bottom, inventoryVm.stockList.filteredStocks.length],
  );

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const activeStockFilterCount = useMemo(() => {
    let count = 0;
    if (inventoryVm.stockList.stockFilter !== 'all') count++;
    if (inventoryVm.stockList.stockCategoryFilter !== 'all') count++;
    if (inventoryVm.stockList.stockBrandFilter) count++;
    if (inventoryVm.stockList.stockSupplierFilter) count++;
    if (inventoryVm.stockList.stockSort !== 'name_asc') count++;
    return count;
  }, [
    inventoryVm.stockList.stockFilter,
    inventoryVm.stockList.stockCategoryFilter,
    inventoryVm.stockList.stockBrandFilter,
    inventoryVm.stockList.stockSupplierFilter,
    inventoryVm.stockList.stockSort,
  ]);

  const openFilterSheet = useCallback(() => {
    setFilterSheetOpen(true);
  }, []);

  const clearStockFiltersPanel = useCallback(() => {
    inventoryVm.stockList.applyStockQuickViewFilter('all');
    inventoryVm.stockList.applyStockCategoryFilter('all');
    inventoryVm.stockList.applyStockBrandFilter(null);
    inventoryVm.stockList.applyStockSupplierFilter(null);
    inventoryVm.stockList.applyStockSort('name_asc');
  }, [inventoryVm.stockList]);

  const showExpiryTracking = useMemo(() => {
    const dash = inventoryVm.global.inventoryDashboard;
    if (!dash) return false;
    return (dash.expiringSoon ?? 0) + (dash.expired ?? 0) > 0;
  }, [inventoryVm.global.inventoryDashboard]);

  const stockStatusFilterOptions = useMemo(() => {
    const dash = inventoryVm.global.inventoryDashboard;
    const ready = dash != null;
    const suffix = (n: number) => (ready ? ` (${n})` : '');
    const noSalesCount = inventoryVm.global.noSalesAlert.count;
    const noSalesDays = inventoryVm.global.noSalesAlert.days;
    const base = [
      { id: 'all', label: 'Todos' },
      { id: 'low', label: `Stock bajo${suffix(dash?.belowMin ?? 0)}` },
      { id: 'zero', label: 'Sin stock' },
      { id: 'negative', label: `Negativo${suffix(dash?.negative ?? 0)}` },
      {
        id: 'no_sales',
        label: `Sin vender ${noSalesDays}d${noSalesCount > 0 ? ` (${noSalesCount})` : ''}`,
      },
    ];
    if (!showExpiryTracking) return base;
    return [
      base[0],
      base[1],
      base[2],
      { id: 'expiring', label: `Vence pronto${suffix(dash?.expiringSoon ?? 0)}` },
      base[3],
      { id: 'expired', label: `Vencidos${suffix(dash?.expired ?? 0)}` },
      base[4],
    ];
  }, [
    inventoryVm.global.inventoryDashboard,
    inventoryVm.global.noSalesAlert.count,
    inventoryVm.global.noSalesAlert.days,
    showExpiryTracking,
  ]);

  const stockCategoryLabel = useMemo(() => {
    if (inventoryVm.stockList.stockCategoryFilter === 'all') return 'Todas';
    const opt = inventoryVm.stockList.inventoryStockCategoryFilters.find(
      (c) => c.id === inventoryVm.stockList.stockCategoryFilter
    );
    return opt?.label ?? 'Categoría';
  }, [inventoryVm.stockList.stockCategoryFilter, inventoryVm.stockList.inventoryStockCategoryFilters]);

  const stockFiltersPanelProps = useMemo(() => ({
    mode: 'stock' as const,
    panelTitle: 'Filtros',
    stockStatusFilters: stockStatusFilterOptions,
    stockStatusFilter: inventoryVm.stockList.stockFilter,
    onStockStatusFilterChange: (v: string) =>
      inventoryVm.stockList.applyStockQuickViewFilter(v as StockStatusFilter),
    categoryLabel: stockCategoryLabel,
    categoryActive: inventoryVm.stockList.stockCategoryFilter !== 'all',
    categoryFilters: inventoryVm.stockList.inventoryStockCategoryFilters,
    categoryFilterId: inventoryVm.stockList.stockCategoryFilter,
    onCategoryFilterChange: (id: string | null | 'all') =>
      inventoryVm.stockList.applyStockCategoryFilter(id ?? 'all'),
    sortOptions: (inventoryVm.global.canViewValuation
      ? STOCK_SORT_OPTIONS
      : STOCK_SORT_OPTIONS.filter((o) => !o.id.startsWith('cost_') && !o.id.startsWith('value_')
      )
    ).filter((o) => showExpiryTracking || (!o.id.startsWith('expiry_'))),
    sortBy: inventoryVm.stockList.stockSort,
    onSortChange: (v: string) => inventoryVm.stockList.applyStockSort(v as StockSort),
    defaultSort: 'name_asc',
    brandFilter: inventoryVm.stockList.stockBrandFilter,
    onBrandFilterChange: inventoryVm.stockList.applyStockBrandFilter,
    brandOptions: inventoryVm.stockList.stockBrandOptions,
    supplierFilter: inventoryVm.stockList.stockSupplierFilter,
    onSupplierFilterChange: inventoryVm.stockList.applyStockSupplierFilter,
    supplierOptions: inventoryVm.stockList.stockSupplierOptions,
  }), [
    stockStatusFilterOptions,
    inventoryVm.stockList.stockFilter,
    inventoryVm.stockList.applyStockQuickViewFilter,
    stockCategoryLabel,
    inventoryVm.stockList.stockCategoryFilter,
    inventoryVm.global.canViewValuation,
    inventoryVm.stockList.inventoryStockCategoryFilters,
    inventoryVm.stockList.applyStockCategoryFilter,
    inventoryVm.stockList.stockSort,
    inventoryVm.stockList.applyStockSort,
    inventoryVm.stockList.stockBrandFilter,
    inventoryVm.stockList.applyStockBrandFilter,
    inventoryVm.stockList.stockBrandOptions,
    inventoryVm.stockList.stockSupplierFilter,
    inventoryVm.stockList.applyStockSupplierFilter,
    inventoryVm.stockList.stockSupplierOptions,
    showExpiryTracking,
  ]);

  const filterTrigger = useMemo(
    () => (
      <FilterSheetButton
        activeCount={activeStockFilterCount}
        onOpen={openFilterSheet}
        variant={isWideInventoryLayout ? 'desk' : 'ticket'}
        style={isWideInventoryLayout ? undefined : inventoryScreenStyles.filterTriggerMobile}
      />
    ),
    [activeStockFilterCount, openFilterSheet, isWideInventoryLayout]
  );

  const headerProps = {
    canEdit: inventoryVm.global.canEdit,
    inventoryDashboard: inventoryVm.global.inventoryDashboard,
    searchQuery: inventoryVm.stockList.searchQuery,
    lookupFeedback: inventoryVm.uiState.lookupFeedback,
    onSearchQueryChange: inventoryVm.stockList.onInventorySearchFieldChange,
    onOpenScanner: () => inventoryVm.uiState.setSearchScannerVisible(true),
    onOpenAddStock: inventoryVm.addStock.openAddStockModal,
    onOpenTransferInfo: inventoryVm.transfers.openTransferEntry,
    onOpenPhysicalCount: inventoryVm.bulkPhysical.openPhysicalCountModal,
    onOpenBulkCoefficient: inventoryVm.bulkPhysical.openBulkCoeffModal,
    // Sin gate de canEdit: el modal CSV ya ofrece «solo exportar» en read-only y
    // el listado filtrado (antes ítem suelto del sheet «Más») vive adentro.
    onOpenCsv: () => setCsvModalVisible(true),
    showBarcodeCameraButton: inventoryVm.global.barcodeAllowCamera,
    onSearchSubmitEditing: inventoryVm.global.barcodeAllowHardware
      ? inventoryVm.global.handleMainSearchHardwareSubmit
      : undefined,
    autoFocusSearchOnWeb: false,
    branchId: inventoryVm.global.currentBranchId ?? null,
    filterTrigger,
    onPickStockSearchSuggestion: inventoryVm.stockList.handlePickInventorySearchSuggestion,
    pinnedStockProduct: inventoryVm.stockList.pinnedStockProduct,
    onClearPinnedStockSearch: inventoryVm.stockList.clearPinnedStockSearch,
    onOpenActivity: inventoryVm.historyActivity.openActivityModal,
    activityLoading: inventoryVm.historyActivity.branchActivityLoading,
    canViewValuation: inventoryVm.global.canViewValuation,
    stockFilter: inventoryVm.stockList.stockFilter,
    onToggleStockFilter: handleToggleStockFilter,
    kpisError: inventoryVm.global.kpisError,
    kpisLoading: inventoryVm.global.kpisLoading,
    onRetryKpis: () => void inventoryVm.global.retryKpis(),
    canTransferBetweenBranches: inventoryVm.transfers.destinationBranches.length > 0,
  };

  // El strip «N productos debajo del mínimo» era redundante: el KPI «Stock bajo»
  // ya muestra el conteo y filtra la lista al tocarlo.
  const noSalesCount = inventoryVm.global.noSalesAlert.count;
  const noSalesDays = inventoryVm.global.noSalesAlert.days;
  const showNoSalesStrip = noSalesCount > 0 && inventoryVm.stockList.stockFilter !== 'no_sales';

  const stockEmptyList = useMemo(
    () =>
      inventoryVm.stockList.stockLoading ? (
        <View style={inventoryScreenStyles.loadingState}>
          <ActivityIndicator color={ticketColors.ink2} />
          <Text style={posUi.checkoutHint}>Cargando stock…</Text>
        </View>
      ) : inventoryVm.stockList.stockError ? (
        // Error ≠ empty: nunca mostrar «Ingresar stock» cuando falló el fetch.
        <ModuleEmptyState
          variant="nest"
          kicker="Stock"
          title="No se pudo cargar el stock"
          subtitle="Revisá tu conexión y probá de nuevo."
          testID="stock.error"
          primaryAction={{
            label: 'Reintentar',
            onPress: () => void inventoryVm.stockList.loadStocks(),
            testID: 'stock.retry',
          }}
        />
      ) : inventoryVm.stockList.stockResultTotalCount > 0 && inventoryVm.stockList.stocks.length === 0 ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Stock"
          title="No hay coincidencias"
          subtitle="Hay productos con este filtro, pero no en esta página. Probá la siguiente o limpiá la búsqueda."
          secondaryAction={
            hasStockSearch
              ? {
                  label: 'Limpiar búsqueda',
                  onPress: () => {
                    inventoryVm.stockList.setSearchQuery('');
                    inventoryVm.stockList.clearPinnedStockSearch();
                  },
                }
              : undefined
          }
        />
      ) : inventoryVm.stockList.stockResultTotalCount === 0 && hasActiveStockQuery ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Stock"
          title={
            inventoryVm.stockList.stockFilter === 'no_sales'
              ? 'Ninguno sin vender'
              : 'No hay resultados'
          }
          subtitle={
            inventoryVm.stockList.stockFilter === 'no_sales'
              ? `En los últimos ${inventoryVm.global.noSalesAlert.days} días todos los que tienen stock se movieron. Bien.`
              : 'Probá otro filtro o otro término de búsqueda.'
          }
          secondaryAction={{
            label: 'Limpiar filtros',
            onPress: clearStockFilters,
          }}
        />
      ) : inventoryVm.stockList.stocks.length === 0 ? (
        inventoryVm.global.catalogProductCount === 0 ? (
          <ModuleEmptyState
            variant="nest"
            kicker="Stock"
            title="No encontramos productos físicos"
            subtitle="Agregá tu primer producto físico desde Catálogo. Después vas a poder ingresar stock acá."
            primaryAction={{ label: 'Ir al catálogo', onPress: inventoryVm.global.openCatalog }}
          />
        ) : inventoryVm.global.catalogProductCount == null ? (
          // null = conteo desconocido (nunca cargó o falló) — no asumir «catálogo vacío».
          <ModuleEmptyState
            variant="nest"
            kicker="Stock"
            title="No pudimos verificar tu catálogo"
            subtitle="Revisá tu conexión y probá de nuevo."
            testID="stock.catalog-count.error"
            primaryAction={{
              label: 'Reintentar',
              onPress: () => {
                void inventoryVm.global.retryCatalogProductCount();
                void inventoryVm.stockList.loadStocks();
              },
              testID: 'stock.catalog-count.retry',
            }}
            secondaryAction={{ label: 'Ir al catálogo', onPress: inventoryVm.global.openCatalog }}
          />
        ) : (
          <ModuleEmptyState
            variant="nest"
            kicker="Stock"
            title="Mirá qué se termina"
            subtitle="Configurá mínimos y te avisamos antes de quedarte sin mercadería."
            primaryAction={
              inventoryVm.global.canEdit
                ? { label: 'Ingresar stock', onPress: inventoryVm.addStock.openAddStockModal }
                : undefined
            }
          />
        )
      ) : (
        <ModuleEmptyState
          variant="nest"
          kicker="Stock"
          title="No hay resultados"
          subtitle="Probá otro filtro o otro término de búsqueda."
          secondaryAction={
            hasActiveStockQuery
              ? {
                  label: 'Limpiar filtros',
                  onPress: clearStockFilters,
                }
              : undefined
          }
        />
      ),
    [
      inventoryVm.stockList.stockLoading,
      inventoryVm.stockList.stockError,
      inventoryVm.stockList.loadStocks,
      inventoryVm.global.retryCatalogProductCount,
      inventoryVm.stockList.stockResultTotalCount,
      inventoryVm.stockList.stocks.length,
      inventoryVm.stockList.stockFilter,
      inventoryVm.global.noSalesAlert.days,
      inventoryVm.global.catalogProductCount,
      inventoryVm.global.canEdit,
      inventoryVm.global.openCatalog,
      inventoryVm.addStock.openAddStockModal,
      inventoryVm.stockList.setSearchQuery,
      inventoryVm.stockList.clearPinnedStockSearch,
      hasActiveStockQuery,
      clearStockFilters,
    ],
  );

  const stockPagination = (
    <TicketDeskFooter
      pageIndex={inventoryVm.stockList.stockPageIndex}
      pageSize={inventoryVm.stockList.stockPageSize}
      productCount={inventoryVm.stockList.stocks.length}
      totalCount={inventoryVm.stockList.stockResultTotalCount}
      hasMore={inventoryVm.stockList.stockHasMore}
      loading={inventoryVm.stockList.stockLoading}
      onPreviousPage={inventoryVm.stockList.loadStockPreviousPage}
      onNextPage={inventoryVm.stockList.loadStockNextPage}
      onGoToPage={inventoryVm.stockList.loadStockJumpToPage}
      embedded
    />
  );

  const stockListSearchEmpty =
    inventoryVm.stockList.stocks.length > 0 && inventoryVm.stockList.filteredStocks.length === 0 ? (
      <ModuleEmptyState
        variant="nest"
        kicker="Stock"
        title="No hay coincidencias"
        subtitle="Esta página no tiene lo que buscás. Limpiá la búsqueda o esperá un toque."
        secondaryAction={{
          label: 'Limpiar búsqueda',
          onPress: () => {
            inventoryVm.stockList.setSearchQuery('');
            inventoryVm.stockList.clearPinnedStockSearch();
          },
        }}
      />
    ) : null;

  const renderStockRows = useCallback(() => {
    const rows = inventoryVm.stockList.filteredStocks;
    if (rows.length === 0) return null;
    const expandedIds = inventoryVm.stockList.expandedStockProductIds;
    const variantRowsByProduct = inventoryVm.stockList.variantRowsByProduct;
    const variantErrors = inventoryVm.stockList.variantRowsErrorByProduct;
    return rows.map((item) => {
      const isVariant = item.variantId != null && item.variantId !== '';
      const isExpanded = expandedIds.has(item.productId);
      const variantError = !isVariant ? variantErrors[item.productId] : undefined;
      // Mantener el chevron visible si el producto ya está expandido o si la
      // última carga de variantes falló — sin chevron el usuario no podría
      // contraer ni reintentar.
      const hasVariants =
        !isVariant &&
        (variantRowsByProduct.has(item.productId) || isExpanded || variantError != null);
      const rowKey = `${item.branchId}:${item.productId}:${item.variantId ?? ''}`;
      return (
        <React.Fragment key={rowKey}>
          <InventoryStockCard
            item={item}
            onDetails={inventoryVm.uiState.openDetailModal}
            showValuation={inventoryVm.global.canViewValuation}
            formatCurrency={formatCurrencyCb}
            isVariant={isVariant}
            hasVariants={hasVariants}
            expanded={isExpanded}
            onToggleExpand={
              hasVariants
                ? () => inventoryVm.stockList.toggleStockProductExpanded(item.productId)
                : undefined
            }
          />
          {variantError != null && isExpanded ? (
            <View
              style={inventoryScreenStyles.variantErrorRow}
              testID={`stock.variant-error.${item.productId}`}
            >
              <Text style={inventoryScreenStyles.variantErrorText}>
                No se pudieron cargar las variantes.
              </Text>
              <TouchableOpacity
                onPress={() => inventoryVm.stockList.retryVariantRows(item.productId)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel="Reintentar carga de variantes"
                testID={`stock.variant-retry.${item.productId}`}
              >
                <Text style={inventoryScreenStyles.variantErrorRetry}>Reintentar</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </React.Fragment>
      );
    });
  }, [
    inventoryVm.stockList.filteredStocks,
    inventoryVm.stockList.expandedStockProductIds,
    inventoryVm.stockList.variantRowsByProduct,
    inventoryVm.stockList.variantRowsErrorByProduct,
    inventoryVm.stockList.retryVariantRows,
    inventoryVm.stockList.toggleStockProductExpanded,
    inventoryVm.uiState.openDetailModal,
    inventoryVm.global.canViewValuation,
    formatCurrencyCb,
  ]);

  const stockNotices = (
    <>
      {showNoSalesStrip ? (
        <TouchableOpacity
          style={inventoryScreenStyles.alertStrip}
          onPress={() => handleToggleStockFilter('no_sales')}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={`Filtrar ${noSalesCount} productos sin vender en ${noSalesDays} días`}
          testID="stock.alert.no-sales"
        >
          <View style={inventoryScreenStyles.alertDot} />
          <View style={inventoryScreenStyles.alertTextWrap}>
            <Text style={inventoryScreenStyles.alertTitle}>
              {noSalesCount}{' '}
              {noSalesCount === 1 ? 'producto sin vender' : 'productos sin vender'} en {noSalesDays} días
            </Text>
            <Text style={inventoryScreenStyles.alertSubtitle}>Ver solo esos</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
        </TouchableOpacity>
      ) : null}

      {inventoryVm.transfers.pendingTransfers.length > 0 ? (
        <InventoryPendingTransfersBanner
          count={inventoryVm.transfers.pendingTransfers.length}
          onPress={inventoryVm.transfers.openPendingTransfersModal}
        />
      ) : null}
    </>
  );

  const stockBody = (
    <PaperSheet style={inventoryScreenStyles.listSheet} testID="stock.list.sheet">
      {inventoryVm.stockList.stocks.length > 0 ? (
        inventoryVm.stockList.filteredStocks.length > 0 ? (
          <>
            {renderStockRows()}
            {stockPagination}
          </>
        ) : (
          stockListSearchEmpty
        )
      ) : (
        stockEmptyList
      )}
    </PaperSheet>
  );

  return (
    <OperationalScrollScreen
      header={<InventoryHeaderSection {...headerProps} layoutWide={isWideInventoryLayout} />}
      notices={stockNotices}
      body={stockBody}
      scrollRef={inventoryVm.stockList.inventoryWideScrollRef}
      refreshing={refreshing}
      onRefresh={onRefresh}
      contentContainerStyle={scrollContentStyle}
      scrollStyle={inventoryScreenStyles.stockList}
      stackGap={inventoryScreenStyles.stack.gap as number | undefined}
      containerStyle={inventoryScreenStyles.container}
      containerCompactStyle={inventoryScreenStyles.containerCompact}
      layoutWide={isWideInventoryLayout}
      fullEmptyState={
        !inventoryVm.global.currentBranchId ? (
          <View style={inventoryScreenStyles.emptyStateFull}>
            <ModuleEmptyState
              variant="nest"
              kicker="Stock"
              title="Seleccioná una sucursal"
              subtitle="Elegí una sucursal para ver el stock."
            />
          </View>
        ) : undefined
      }
      modals={
        <>
          <FilterSheetModal
            visible={filterSheetOpen}
            onClose={() => setFilterSheetOpen(false)}
            onApply={() => setFilterSheetOpen(false)}
            onClear={clearStockFiltersPanel}
            hasActiveDraft={activeStockFilterCount > 0}
            appearance="ticket"
            kicker="Stock"
            subtitle="Acotá la lista de productos."
          >
            <PosCatalogStockFiltersPanel
              {...stockFiltersPanelProps}
              sheetMode
            />
          </FilterSheetModal>

          <InventoryScreenModals
            inventoryVm={inventoryVm}
            layoutWide={isWideInventoryLayout}
            csvModalVisible={csvModalVisible}
            onCloseCsvModal={() => setCsvModalVisible(false)}
          />
        </>
      }
    />
  );
};
